import { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, AlertCircle } from "lucide-react";
import { isAxiosError } from "axios";
import { verifyMfa } from "@/api/mfa";
import { useAuthStore } from "@/state/authStore";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type InputMode = "totp" | "backup";

export default function MfaVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const destination = (location.state as { from?: string })?.from ?? "/engage";

  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clearMfaFlags = useAuthStore(s => s.clearMfaFlags);

  const [mode, setMode] = useState<InputMode>("totp");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await verifyMfa({ code, use_backup_code: mode === "backup" });
      clearMfaFlags();
      navigate(destination, { replace: true });
    } catch (err) {
      setCode("");
      if (isAxiosError(err) && err.response?.status === 429) {
        setError("Too many attempts. Please wait a minute and try again.");
      } else {
        setError(
          mode === "totp"
            ? "Invalid code. Please try again."
            : "Invalid backup code."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-primary/10 p-3">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center font-bold">
            Two-Factor Verification
          </CardTitle>
          <CardDescription className="text-center">
            {mode === "totp"
              ? "Enter the 6-digit code from your authenticator app."
              : "Enter one of your saved backup codes."}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "totp" ? (
              <input
                key="totp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                onChange={e => {
                  setCode(e.target.value.replace(/\D/g, ""));
                  if (error) setError(null);
                }}
                placeholder="000000"
                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-ring bg-background"
                required
                autoFocus
              />
            ) : (
              <input
                key="backup"
                type="text"
                autoComplete="off"
                value={code}
                onChange={e => {
                  setCode(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="xxxxx-xxxxx"
                className="w-full px-4 py-3 text-center text-lg font-mono border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-ring bg-background"
                required
                autoFocus
              />
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || code.length === 0}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Verifying...
                </span>
              ) : (
                "Verify"
              )}
            </Button>
          </form>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setMode(prev => (prev === "totp" ? "backup" : "totp"));
                setCode("");
                setError(null);
              }}
              className="text-sm text-primary hover:underline"
            >
              {mode === "totp"
                ? "Use a backup code instead"
                : "Use authenticator app instead"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
